import { EditIcon } from '@chakra-ui/icons'
import { Badge, Box, Checkbox, Divider, Flex, Text } from '@chakra-ui/react'
import { RoundedButton } from '@components/Buttons/ActionButton'
import { PatientMedicationsModal } from '@components/Gyn/PatientMedicationsModal'
import { useProfile } from '@contexts/user/ProfileContext'
import { GynMedicationsAndSupplementsData } from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { useField, useForm } from 'react-final-form'
import { SectionLabel } from '../../SectionLabel'

const formatAdministeredVia: Record<string, string> = {
  oral: 'Oral',
  sublingual: 'Sublingual',
  im: 'IM',
  iv: 'IV',
  topical: 'Topical',
  rectal: 'Rectal',
  vaginal: 'Vaginal',
  subcutaneous: 'Subcutaneous',
  inhalation: 'Inhalation',
  ophthalmic: 'Ophthalmic',
  intrauterine: 'Intrauterine',
  buccal: 'Buccal',
}

const MedicationRows = ({ data }: { data: GynMedicationsAndSupplementsData | undefined }) => {
  const activeMeds = useMemo(() => (data?.medications ?? []).filter(m => m.currentlyTaking), [data])
  const activeSupplements = useMemo(
    () => (data?.supplements ?? []).filter(s => s.currentlyTaking),
    [data],
  )

  if (!activeMeds.length && !activeSupplements.length) {
    return (
      <Text fontSize="sm" color="gray.500" py={1}>
        No medications or supplements on file.
      </Text>
    )
  }

  return (
    <Box>
      {activeMeds.length > 0 && (
        <Box mb={activeSupplements.length ? 2 : 0}>
          <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" mb={1}>
            Medications
          </Text>
          <Flex flexWrap="wrap" gap={2}>
            {activeMeds.map((med, i) => (
              <Badge
                key={i}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="sm"
                colorScheme="blue"
                variant="subtle"
                // Badges default to nowrap — long med strings must wrap on narrow screens.
                whiteSpace="normal"
                maxW="100%"
                fontWeight={600}>
                {med.name}
                {med.dosage ? ` ${med.dosage}` : ''}
                {med.frequency ? ` — ${med.frequency}` : ''}
                {med.administeredVia
                  ? ` (${formatAdministeredVia[med.administeredVia] ?? med.administeredVia})`
                  : ''}
              </Badge>
            ))}
          </Flex>
        </Box>
      )}
      {activeMeds.length > 0 && activeSupplements.length > 0 && <Divider my={2} />}
      {activeSupplements.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" mb={1}>
            Supplements
          </Text>
          <Flex flexWrap="wrap" gap={2}>
            {activeSupplements.map((sup, i) => (
              <Badge
                key={i}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="sm"
                colorScheme="teal"
                variant="subtle"
                whiteSpace="normal"
                maxW="100%"
                fontWeight={600}>
                {sup.name}
                {sup.dosage ? ` ${sup.dosage}` : ''}
                {sup.frequency ? ` — ${sup.frequency}` : ''}
              </Badge>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  )
}

export const ExamMedicationsInput = () => {
  const { user } = useProfile()
  const form = useForm()
  const [modalOpen, setModalOpen] = useState(false)

  const {
    input: { value: reviewed, onChange },
  } = useField<boolean | undefined>('data.medicationsReviewed', { subscription: { value: true } })

  const medsData = user?.gyn?.mergedData?.medicationsAndSupplements

  const handleSaved = useCallback(() => {
    form.change('data.medicationsReviewed', true)
    setModalOpen(false)
  }, [form])

  return (
    <Box w="100%" px={4} py={3}>
      <Flex w="100%" pb={1} gap={3} align="center">
        <SectionLabel>Medications &amp; Supplements</SectionLabel>
        <RoundedButton
          size="xs"
          borderRadius="full"
          leftIcon={<EditIcon />}
          onClick={() => setModalOpen(true)}>
          Edit
        </RoundedButton>
      </Flex>
      <MedicationRows data={medsData} />
      <Box py={1} px={2} data-child-name="data.medicationsReviewed">
        <Checkbox
          isChecked={!!reviewed}
          onChange={e => onChange(e.target.checked)}
          colorScheme="green"
          size="sm">
          <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.300' }}>
            Medications and supplements reviewed — no changes since last visit
          </Text>
        </Checkbox>
      </Box>
      <PatientMedicationsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
    </Box>
  )
}
